<template>
  <div class="content-area">
    <hero-bar>
      #{{ id }}
      <b-button
        v-if="checkPermission('create_transfer')"
        slot="right"
        icon-left="content-save"
        type="is-link"
        size="is-medium"
        :class="{'is-loading':isLoading}"
        @click="saveDetails"
      >
        {{ $t('save') }}
      </b-button>
    </hero-bar>

    <b-loading :is-full-page="true" :active.sync="isFullLoading" :can-cancel="false" />

    <section v-if="data" class="section is-main-section transfer-details">
      <div class="columns">
        <!-- head details -->
        <card-component
          title="Taşıt Görev Bilgileri"
          :has-button-slot="true"
          class="mr-10 bfy-column w-50"
        >
          <a v-if="data.car" slot="button" :href="urlForLiveTracking" target="_blank" class="button">
            <b-tooltip slot="right" label="Canlı İzle" type="is-dark" position="is-bottom">
              <b-icon icon="crosshairs-gps" custom-size="mdi-24px" />
            </b-tooltip>
          </a>
          <a slot="button" :href="urlForGoogleDirections" target="_blank" type="button" class="button">
            <b-tooltip slot="right" :label="$t('directions')" type="is-dark" position="is-bottom">
              <b-icon icon="directions" custom-size="mdi-24px" />
            </b-tooltip>
          </a>
          <b-field horizontal label="Durum" class="no-wrap">
            <div>
              <span class="tag" :class="data.status | showStatus">
                {{ $t("status." + data.status) }}
              </span>
              <b-button
                v-if="checkPermission('set_driver') && isVoidable"
                size="is-small"
                type="is-danger"
                class="ml-10"
                icon-left="close-circle"
                :loading="isLoadingReject"
                @click="isRejectModalActive = !isRejectModalActive"
              >
                {{ $t('cancelRequest') }}
              </b-button>
            </div>
          </b-field>
          <b-field horizontal :label="$t('start')" class="no-wrap">
            {{ fromAddress }}
          </b-field>
          <b-field horizontal :label="$t('target')" class="no-wrap">
            {{ toAddress }}
          </b-field>
          <b-field horizontal :label="$t('vehicleDutyDate')" class="no-wrap">
            <b-tooltip :label="data.transfer_date" type="is-dark" position="is-bottom">
              <span>{{ data.transfer_date | formatDateTimeYear }}</span>
            </b-tooltip>
          </b-field>
          <b-field horizontal label="Görev Türü" class="no-wrap">
            {{ data.duty_type_specification.name }}
          </b-field>
          <b-field horizontal label="Araç Türü" class="no-wrap">
            {{ data.vehicle_type.name }}
          </b-field>
          <b-field horizontal label="Talep Eden Birim" class="no-wrap">
            {{ data.request_unit_specification.name }}
          </b-field>
          <b-field v-if="isFieldVisible('transfer', 'requested_unit_id') && data.requested_unit_specification" horizontal label="Talep Edilen Birim" class="no-wrap">
            {{ data.requested_unit_specification && data.requested_unit_specification.name}}
          </b-field>
          <b-field horizontal label="Talep Eden Ad & Soyad" class="no-wrap">
            <span>{{ data.demandant_name }}</span>
          </b-field>
          <b-field horizontal label="Talep Eden Telefon" class="no-wrap">
            <a :href="'tel:' + data.demandant_phone">{{ data.demandant_phone | phone }}</a>
          </b-field>
          <b-field v-if="data.status == 5 && data.reject_comment" horizontal :label="$t('rejectComment')" class="no-wrap">
            {{ data.reject_comment }}
          </b-field>
          <b-field v-if="data.status == 5 && data.reject_user" horizontal :label="$t('rejectUser')" class="no-wrap">
            {{ data.reject_user.name }}
            <span class="emptyText">({{ data.reject_user.role.name }})</span>
          </b-field>
          <b-field horizontal label="Oluşturulma Tarihi" class="no-wrap">
            {{ data.created_at | formatDateTimeYear }}
          </b-field>
          <b-field horizontal label="Oluşturan Kullanıcı" class="no-wrap">
            {{ data.user.name }}
          </b-field>
        </card-component>

        <card-component
          v-if="checkPermission('set_driver')"
          title="Sürücü ve Araç Bilgileri"
          class="mr-10 bfy-column w-40"
        >
          <b-field horizontal :label="$t('estimatedTime')" class="no-wrap">
            <b-input
              v-model="estimated_time"
              :placeholder="$t('estimatedTime')"
              :disabled="!isVoidable"
              type="number"
              min="1"
              step="0.01"
            />
          </b-field>
          <b-field horizontal :label="$t('driver')" class="no-wrap">
            <b-autocomplete
              v-model="fakeDriver"
              :disabled="estimated_time < 1 || data.self_drive || !isVoidable"
              field="name"
              :data="drivers"
              :placeholder="$t('chooseDriver')"
              :loading="isDriverFetching"
              @typing="getAsyncDriverData"
              @select="selectDriver"
            >
              <div slot-scope="props">
                <div class="media">
                  <div class="media-content">
                    <b>{{ props.option.name }}</b>
                    <br>
                    <small>
                      {{ props.option.phone | phone }}
                    </small>
                  </div>
                </div>
              </div>
            </b-autocomplete>
          </b-field>
          <b-field horizontal :label="$t('driverSecond')" class="no-wrap">
            <b-autocomplete
              v-model="fakeDriverSecond"
              :disabled="estimated_time < 1 || data.self_drive || !isVoidable"
              field="name"
              :data="drivers"
              :placeholder="$t('chooseDriverSecond')"
              :loading="isDriverSecondFetching"
              @typing="getAsyncSecondDriverData"
              @select="selectSecondDriver"
            >
              <div slot-scope="props">
                <div class="media">
                  <div class="media-content">
                    <b>{{ props.option.name }}</b>
                    <br>
                    <small>
                      {{ props.option.phone | phone }}
                    </small>
                  </div>
                </div>
              </div>
            </b-autocomplete>
          </b-field>
          <b-field horizontal :label="$t('carTxt')" class="no-wrap">
            <b-autocomplete
              v-model="fakeCarPlate"
              field="plate"
              class="leading-none"
              :data="cars"
              :placeholder="$t('chooseCar')"
              :loading="isCarFetching"
              :disabled="estimated_time < 1 || !isVoidable"
              @typing="getAsyncCarData"
              @select="selectCar"
            >
              <template slot-scope="props">
                {{ props.option.plate }}
                <span class="emptyText">
                  ({{ props.option.brand }} - {{ props.option.model }})
                  <br>
                  <small>{{ props.option.vehicle_type ? props.option.vehicle_type.name : '-' }}</small>
                </span>
              </template>
            </b-autocomplete>
          </b-field>
          <b-field v-if="driver.phone" horizontal label="Sürücü Tel." class="no-wrap">
            <b-input
              :value="driver.phone | phone"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <b-field v-if="data.start_km" horizontal label="Başlangıç KM" class="no-wrap">
            <b-input
              :value="data.start_km"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <b-field v-if="data.end_km" horizontal label="Bitiş KM" class="no-wrap">
            <b-input
              :value="data.end_km"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <b-message
            v-for="(carSuggestion, index) in suggestions.cars"
            :key="index"
            type="is-info"
            class="message-p10"
          >
            Varış noktasına {{ parseInt(carSuggestion.dist) }} metre uzaklıkta
            <router-link
              :to="{ name: 'transfers.show', params: { id: carSuggestion.id }}"
              target="_blank"
            >
              #{{ carSuggestion.id }}
            </router-link>
            nolu istekte {{ carSuggestion.car_plate }} aracı {{ carSuggestion.estimated_time | showOnlyTime }} de boşa çıkacaktır.
          </b-message>
        </card-component>

        <card-component
          v-else
          title="Sürücü ve Araç Bilgileri"
          class="mr-10 bfy-column w-30"
        >
          <b-field horizontal :label="$t('driver')" class="no-wrap">
            <b-input
              :value="driver.name"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <b-field horizontal label="Sürücü Tel." class="no-wrap">
            <b-input
              :value="driver.phone | phone"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <b-field v-if="settings('general.driverCarAndVehicleSelectableWithDriverRole') && data.car_plate" horizontal :label="$t('plate')" class="no-wrap">
            <b-input
              :value="data.car_plate"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <!-- <b-field v-if="data.car" horizontal :label="$t('plate')" class="no-wrap">
            <div>
              {{ data.car.plate }}
              <br>
              <span class="emptyText">({{ data.car.brand }} - {{ data.car.model }})</span>
            </div>
          </b-field> -->
          <b-field v-if="data.start_km" horizontal label="Başlangıç KM" class="no-wrap">
            <b-input
              :value="data.start_km"
              custom-class="is-static"
              readonly
            />
          </b-field>
          <b-field v-if="data.end_km" horizontal label="Bitiş KM" class="no-wrap">
            <b-input
              :value="data.end_km"
              custom-class="is-static"
              readonly
            />
          </b-field>
        </card-component>

        <driver-panel
          v-if="checkDriver"
          :transfer="data"
        />

        <admin-panel
          v-if="checkAdmin"
          :reject-modal-active.sync="isRejectModalActive"
          :approve-loading="isLoadingApproval"
          :set-status="setStatus"
          :data="data"
        />
      </div><!-- head details -->
      <!-- Details -->
      <card-component icon="account-card-details">
        <tiles>
          <b-field label="Oluşturan Kullanıcı Açıklaması" class="is-fullwidth">
            <b-input
              v-model="data.user_description"
              :value="data.user_description"
              :disabled="data.status != 0 || authUser.id != data.user.id"
              type="textarea"
              maxlength="200"
              custom-class="is-static is-fullwidth"
            />
          </b-field>
          <b-field v-if="checkPermission('see_admin_note')" label="Baş Şoför Açıklaması" class="is-fullwidth">
            <b-input
              v-model="data.lead_driver_description"
              :value="data.lead_driver_description"
              :disabled="!checkPermission('write_admin_note') || !isVoidable"
              type="textarea"
              maxlength="200"
              custom-class="is-static is-fullwidth"
            />
          </b-field>
        </tiles>
      </card-component><!-- End Details -->
      <!-- Additional Passengers -->
      <div v-if="data.passengers.length > 0" class="flex-left">
        <div
          v-for="(passenger, index) in data.passengers"
          :key="index"
          class="passenger_box"
        >
          <card-component
            :title="(index + 1) +'. '+ $t('passenger')"
          >
            <b-field :label="$t('fullname')">
              <span> {{ passenger.name }} </span>
            </b-field>
            <b-field :label="$t('phoneNumber')">
              <a :href="'tel:' + passenger.phone">{{ passenger.phone | phone }}</a>
            </b-field>
          </card-component>
        </div>
      </div>
      <!-- / Additional Passengers -->
    </section>
    <b-modal
      :active.sync="isRejectModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <RejectModal
        :id="id"
        :button-text="checkAdmin ? $t('reject') : $t('cancelRequest')"
      />
    </b-modal>
    <b-modal
      :active.sync="isMoveShuttleModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card" style="width: 700px">
        <ShuttleRegister
          :isModal="true"
          :transferDetails="data"
          :transferId="id"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapState, mapGetters } from 'vuex'

import AdminPanel from '@/components/TransferPanels/AdminPanel'
import DriverPanel from '@/components/TransferPanels/DriverPanel'

export default {
  name: 'TransferDetails',
  title: ({ $i18n }) => $i18n.t('vehicleDuties'),
  components: {
    HeroBar: () => import('@/components/HeroBar'),
    CardComponent: () => import('@/components/CardComponent'),
    Tiles: () => import('@/components/Tiles'),
    RejectModal: () => import(/* webpackPrefetch: true */ '@/components/RejectModal'),
    ShuttleRegister: () => import(/* webpackPrefetch: true */ '@/views/ShuttleRegister'),
    AdminPanel: AdminPanel,
    DriverPanel: DriverPanel,
  },
  props: {
    id: {
      type: Number,
      default: function () {
        return parseInt(this.$route.params.id)
      }
    },
    driverId: {
      type: Number,
      default: function () {
        return parseInt(this.$route.query.driver_id);
      }
    },
    secondDriverId: {
      type: Number,
      default: function () {
        return parseInt(this.$route.query.second_driver_id);
      }
    },
    carId: {
      type: Number,
      default: function () {
        return parseInt(this.$route.query.car_id);
      }
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      data: null,
      isRejectModalActive: false,
      isMoveShuttleModalActive: false,
      driver: { phone: '', name: '' },
      secondDriver: { phone: '', name: '' },
      isDriverFetching: false,
      isSecondDriverFetching: false,
      drivers: [],
      isCarFetching: false,
      cars: [],
      fakeCarPlate: '',
      fakeDriver: '',
      fakeDriverSecond: '',
      isFullLoading: true,
      isLoading: false,
      isLoadingReject: false,
      isLoadingApproval: false,
      isLoadingMoveShttle: false,
      changeDisable: false,
      estimated_time: null,
      minutesGranularity: 5,
      suggestions: []
    }
  },
  computed: {
    ...mapState([
      'bfyHost',
    ]),
    ...mapGetters({
      settings: 'settings/settings',
      isFieldVisible: 'settings/isFieldVisible',
      authUser: 'authUser'
    }),
    checkAdmin () {
      if (!this.$store.getters.authUser.attributes) {
        return false
      }

      if (this.data?.self_drive && this.data.driver.id == this.$store.getters.authUser.id) {
        return false
      }

      return this.$store.getters.authUser.attributes.role === 'Admin'
    },
    checkApproveReject () {
      if (!this.$store.getters.authUser.attributes) {
        return false
      }

      const checkStatus = this.data.status == 2
      console.log('checkStatus => ', checkStatus);
      return this.checkAdmin && checkStatus;
    },
    checkDriver () {
      if (!this.authUser.attributes) {
        return false
      }

      return this.authUser.attributes.role === 'Driver' || this.authUser.id == this.driver.id
    },
    checkReject () {
      if (!this.$store.getters.authUser.attributes) {
        return false
      }

      if (this.$store.getters.authUser.attributes.role == 'Admin') {
        if (this.data.status == 5 || this.data.status == 3 || this.data.status == 4 || this.data.status == 2) {
          return true
        }
      }
      return false
    },
    isVoidable () {
      return [0, 1, 2].includes(parseInt(this.data.status))
    },
    fromAddress () {
      let address = []

      if (this.data.from_neighborhood) {
        address = [this.data.from_neighborhood.name + ' mah.']
      }

      if (this.data.from_address_line) {
        address = [...address, this.data.from_address_line + ',']
      }

      if (this.data.from_district) {
        address = [...address, this.data.from_district.name, '/']
      }

      address = [...address, this.data.from_province.name]

      return address.join(' ')
    },
    toAddress () {
      let address = []

      if (this.data.to_neighborhood) {
        address = [this.data.to_neighborhood.name + ' mah.']
      }

      if (this.data.to_address_line) {
        address = [...address, this.data.to_address_line + ',']
      }

      if (this.data.to_district) {
        address = [...address, this.data.to_district.name, '/']
      }

      address = [...address, this.data.to_province.name]

      return address.join(' ')
    },
    urlForGoogleDirections () {
      return 'https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=' + this.fromAddress + '&destination=' + this.toAddress
    },
    urlForLiveTracking () {
      return `${this.bfyHost}/takip/harita`;
    },
  },
  beforeDestroy () {
    this.$store.commit('setAsideActiveMenuKey', '')
  },
  async mounted () {
    this.loadTransferDetails()
    this.$store.commit('setAsideActiveMenuKey', 'transfers')
  },
  methods: {
    loadTransferDetails () {
      this.showSpinner();
      this.$http
        .get(`transfers/${this.id}`)
        .then(({ data }) => {
          this.data = data.data;
          this.fakeCarPlate = this.data.car_plate;

          if (data.data.driver) {
            this.driver = data.data.driver;
            this.fakeDriver = this.driver.name;
          }

          if (data.data.second_driver) {
            this.secondDriver = data.data.second_driver;
            this.fakeDriverSecond = this.secondDriver.name;
          }

          this.estimated_time = data.data.estimated_time

          if (data.data.status == 5) {
            this.changeDisable = true
          }

          this.$nextTick(() => {
            this.checkSuggestions()
            this.loadCarDriver()
            this.hideSpinner()
            this.isFullLoading = false
          });
      });
    },
    getAsyncDriverData: debounce(function (name) {
      this.drivers = []

      if (!name.length) {
        return
      }

      this.isDriverFetching = true

      this.$http.get('available-drivers', {
        params: {
          transfer_id: this.id,
          estimated_time: this.estimated_time,
          name,
        }
      }).then(({ data }) => {
        this.drivers = data.data
      }).catch(() => {
        this.drivers = []
      }).finally(() => {
        this.isDriverFetching = false
      })
    }, 500),

    getAsyncSecondDriverData: debounce(function (name) {
      this.drivers = []

      if (!name.length) {
        return
      }

      this.isSecondDriverFetching = true

      this.$http.get('available-drivers', {
        params: {
          transfer_id: this.id,
          estimated_time: this.estimated_time,
          name,
        }
      }).then(({ data }) => {
        this.drivers = data.data
      }).catch(() => {
        this.drivers = []
      }).finally(() => {
        this.isSecondDriverFetching = false
      })
    }, 500),
    getAsyncCarData: debounce(function (name) {
      this.cars = []

      if (!name.length) {
        return
      }

      this.isCarFetching = true

      this.$http.get('list-cars', {
        params: {
          transfer_id: this.id,
          estimated_time: this.estimated_time,
          q: name,
        }
      }).then(({ data }) => {
        this.cars = data
      }).catch(() => {
        this.cars = []
      }).finally(() => {
        this.isCarFetching = false
      })
    }, 500),
    selectDriver (option) {
      if (option) {
        this.driver = option;
        this.drivers = [];
        // this.fakeDriver = option.attributes.name;

        this.$http.post('check-driver', {
          driver_id: option.id,
          transfer_id: this.id
        }).then(r => {
            if (r.data.count > 0) {
              this.$buefy.toast.open({
                message: this.$t('driverWarning', [r.data.count]),
                type: 'is-warning',
                duration: 5000,
                queue: false
              });
            }
        })
      }
    },
    selectSecondDriver (option) {
      if (option) {
        this.secondDriver = option;
        this.drivers = [];

        this.$http.post('check-driver', {
          driver_id: option.id,
          transfer_id: this.id
        }).then(r => {
            if (r.data.count > 0) {
              this.$buefy.toast.open({
                message: this.$t('driverWarning', [r.data.count]),
                type: 'is-warning',
                duration: 5000,
                queue: false
              });
            }
        })
      }
    },
    selectCar (option) {
      if (option) {
        this.data.car_plate = option.plate;
        this.cars = [];

        this.$http.post('check-car', {
          plate: option.plate,
          transfer_id: this.id
        }).then(r => {
          if (r.data.count > 0) {
            this.$buefy.toast.open({
              message: this.$t('carWarning', [r.data.count]),
              type: 'is-warning',
              duration: 5000,
              queue: false
            });
          }
        })
      }
    },
    setStatus (status) {
      if (status == 1 || status == 3 || status == 4) {
        this.isLoadingApproval = true
      }
      this.isLoading = true;

      this.$http.put('set-status', {
        id: this.id,
        status: status,
      }).then(r => {
        if (r.data.success) {
          this.data.status = r.data.status;

          if (this.isLoadingApproval) {
            this.$buefy.toast.open({
              message: this.$t('approved'),
              type: 'is-success',
              queue: false
            });
            this.isLoadingApproval = false;
          }

          if (r.data.status == 4 || r.data.status == 1) {
           this.goBack()
          }
        }
        this.isLoading = false;
      })
      .catch(() => {
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Error: ${e.message}',
          position: 'is-bottom-right',
          type: 'is-danger',
          hasIcon: true
        })
        this.$buefy.toast.open({
          message: 'Error:',
          type: 'is-danger',
          queue: false
        })
      });
    },
    saveDetails () {
      this.isLoading = true;
      this.saveAction();
    },
    saveAction (status) {
      if (!this.driver) {
        this.driver = { id: null };
      }
      if (!this.secondDriver) {
        this.secondDriver = { id: null }
      }

      this.$http.put('edit-transfer', {
          id: this.id,
          driver_id: this.driver.id,
          second_driver_id: this.secondDriver.id,
          car_plate: this.data.car_plate,
          status: status,
          user_description: this.data.user_description,
          lead_driver_description: this.data.lead_driver_description,
          estimated_time: this.estimated_time,
        }).then(r => {
          if (r.data.success) {
            if (this.isLoading) {
              this.$buefy.toast.open({
                message: this.$t('updated'),
                type: 'is-success',
                queue: false
              });

              this.goBack()

            } else if (this.isLoadingApproval) {
              this.$buefy.toast.open({
                message: this.$t('approved'),
                type: 'is-success',
                queue: false
              });

              this.goBack()

            } else if (this.isLoadingReject) {
              this.$buefy.toast.open({
                message: this.$t('canceled'),
                type: 'is-warning',
                queue: false
              });

              this.goBack()
            }

            if (r.data.status) {
              this.data.status = r.data.status;
            }
          } else {
            if (r.data.status) {
              this.data.status = r.data.status;

              if (r.data.status == 5) {
                this.changeDisable = true
              }
              this.data.reject_comment = r.data.reject_comment
              this.data.reject_user = {
                data: {
                  attributes: {
                    name: r.data.reject_user.name,
                    role: r.data.reject_user.roles[0].name
                  }
                }
              }
            }
            this.$buefy.toast.open({
              message: this.$t('errorApproved'),
              type: 'is-danger',
              queue: false
            })
          }
        })
        .catch(e => {
          this.$buefy.notification.open({
            duration: 5000,
            message: `Error: ${e.message}`,
            position: 'is-bottom-right',
            type: 'is-danger',
            hasIcon: true
          })
          this.$buefy.toast.open({
            message: `Error: ${e.message}`,
            type: 'is-danger',
            queue: false
          })
        })
        .finally(() => {
          this.isLoadingApproval = false;
          this.isLoadingReject = false;
          this.fullLoading = false;
        });
    },
    checkSuggestions () {
      if (!this.checkPermission('set_driver') || this.data.status != 1) {
        return;
      }

      this.$http.get(`suggestions/${this.id}`)
        .then(({ data }) => {
          this.suggestions = data;
        })
    },
    loadCarDriver () {
      if (this.driverId) {
        this.isDriverFetching = true
        this.$http.get(`edit-user?id=${this.driverId}`)
          .then(({ data }) => {
            this.fakeDriver = data.data.attributes.name;
            this.selectDriver(data.data)
          })
          .finally(() => {
            this.isDriverFetching = false
          })
      }

      if (this.secondDriverId) {
        this.isSecondDriverFetching = true
        this.$http.get(`edit-user?id=${this.secondDriverId}`)
          .then(({ data }) => {
            this.fakeDriverSecond = data.data.attributes.name;
            this.selectDriver(data.data)
          })
          .finally(() => {
            this.isSecondDriverFetching = false
          })
      }

      if (this.carId) {
        this.isCarFetching = true
        this.$http.get(`car/${this.carId}`)
          .then(({ data }) => {
            this.fakeCarPlate = data.plate;
            this.selectCar(data)
          })
          .finally(() => {
            this.isCarFetching = false
          })
      }
    },
    goBack () {
      if (this.isModal) {
        this.$store.commit('boardRefresh', true);
        this.$parent.close();
        return;
      }

      this.$router.push({ name: 'transfers.index' });
    }
  }
}
</script>

<style>
  .leading-none .dropdown-item {
    line-height: 1;
  }
</style>
